import { computed, ref } from "vue";
import axios from "axios";
import { defineStore, storeToRefs } from "pinia";
import { isEqual } from "lodash";
import CustomStore from "devextreme/data/custom_store";
import { useCabinetStore } from "@/stores/cabinetStore.js";
import { useUserStore } from "@/stores/userStore.js";
import { useToastStore } from "@/stores/toastStore.js";

export const useSearchPageStore = defineStore('search-page-store',  () => {
  const cabinetStore = useCabinetStore()
  const { cabinetId } = storeToRefs(cabinetStore)
  const userStore = useUserStore()
  const { userId } = storeToRefs(userStore)
  const toastStore = useToastStore()

  const searchGridInstance = ref(null)
  const searchResultCount = ref(0)
  const basketCount = ref(0)

  const selectedOnly = ref(false)
  const headerFilter = ref()
  const searchBarObjects = ref([])

  const docQueryParams = computed(() => {
    return {
      selected_only: selectedOnly.value,
      header_filter: headerFilter.value,
      search_bar_objects: searchBarObjects.value,
    }
  })

  const activeDocQueryParams = ref(null)

  const setDocQueryParams = () => {
    activeDocQueryParams.value = docQueryParams.value
  }

  const resetSearchAndFilter = () => {
    selectedOnly.value = false
    headerFilter.value = null
    searchBarObjects.value = []
    searchGridInstance.value?.clearSorting()
  }

  const refreshData = () => {
    searchGridInstance.value.refresh()
  }

  const refreshSearchResults = () => {
    lastParams.value = null
    refreshData()
  }

  const scrollToTop = () => {
    searchGridInstance.value.pageIndex(0)
  }

  const tagFilterItems = ref([])
  const searchHistoryItems = ref([])
  const selectAllState = ref(false)
  const tagEditorItems = ref([])

  async function fetchTagFilterItems() {
    if (!cabinetId.value) return
    const r = await axios.get("/api/search_grid/tag_filter_items", { params: { cabinet_id: cabinetId.value } })
    tagFilterItems.value = r.data
  }

  async function fetchSearchHistoryItems() {
    if (!cabinetId.value) return
    const r = await axios.get("/api/search_grid/search_history_items", { params: { cabinet_id: cabinetId.value } })
    searchHistoryItems.value = r.data
  }

  async function fetchSelectAllState() {
    if (!cabinetId.value) return
    const r = await axios.get("/api/search_grid/select_all_state", { params: { cabinet_id: cabinetId.value } })
    selectAllState.value = r.data.state
  }

  async function fetchTagEditorItems() {
    if (!cabinetId.value || !userId.value) return
    const r = await axios.get("/api/tag_editor_items", {
      params: { mode: "user_selections", user_id: userId.value, cabinet_id: cabinetId.value }
    })
    tagEditorItems.value = r.data
  }

  async function fetchBasketCount() {
    if (!cabinetId.value) return
    const r = await axios.get("/api/basket_count", { params: { cabinet_id: cabinetId.value } })
    basketCount.value = r.data
  }

  function refreshSearchPageSidebarData() {
    fetchTagFilterItems()
    fetchSearchHistoryItems()
    fetchSelectAllState()
    fetchTagEditorItems()
    fetchBasketCount()
  }

  const selectDocument = (docId, selected, cabinetId) => {
    const params = {
      doc_id: docId,
      selected: selected,
      cabinet_id: cabinetId
    }

    axios.post('/api/select_document', params).then(_response => {
      // update the cached data so that when you scroll down and back up you don't lose the change
      searchGridDataStore.push([{
        type: 'update',
        key: docId,
        data: { selected: selected }
      }])
      fetchBasketCount()
      fetchSelectAllState()
      fetchTagEditorItems()
    })
  }

  const selectAll = (selected, cabinetId) => {
    const params = {
      selected: selected,
      cabinet_id: cabinetId
    }

    axios.post('/api/select_all', params).then(_response => {
      refreshData() // update the data so that cached checkbox states are cleared
      fetchBasketCount()
      fetchSelectAllState()
      fetchTagEditorItems()
    })
  }

  const editTags = (modeParams, tagsToAdd, tagsToRemove) => {
    const params = {
      mode_params: modeParams,
      tags_to_add: tagsToAdd,
      tags_to_remove: tagsToRemove
    }

    axios.post('/api/edit_tags', params).then(r => {
      refreshSearchResults()
      fetchTagFilterItems()
      fetchTagEditorItems()
      toastStore.showToastMessage('Tags updated', 'success', 'add')
    })
  }

  const editMetadata = (modeParams, fields) => {
    const params = {
      mode_params: modeParams,
      fields: fields
    }

    axios.post('/api/edit_metadata', params).then(r => {
      refreshSearchResults()
      toastStore.showToastMessage('Metadata updated', 'success', 'add')
    })
  }

  const fetchHeadlines = async (docId, searchStrings) => {
    const params = {
      doc_id: docId,
      search_strings: searchStrings
    }

    const response = await axios.get('/api/headline', { params })
    return response.data.headlines
  }

  const fetchFoldersByPath = async (currentPath) => {
    const params = {
      current_path: currentPath,
      cabinet_id: cabinetId.value
    }

    const response = await axios.get('/api/folders_by_path', { params })
    return response.data
  }

  const emptyBasket = () => {
    const params = {
      cabinet_id: cabinetId.value
    }

    axios.post('/api/empty_basket', params).then(_response => {
      refreshData()
      fetchBasketCount()
      fetchSelectAllState()
      fetchTagEditorItems()
    })
  }

  const generateBasket = async () => {
    const params = {
      cabinet_id: cabinetId.value
    }

    const response = await axios.post('/api/generate_basket', params)
    return response.data.basket_id
  }

  // params used in the last load to detect if need to start a new search or not
  const lastParams = ref(null)
  const searchGridDataStore = new CustomStore({
    key: "doc_id",
    load: async (loadOptions) => {
      const params = {
        cabinet_id: cabinetId.value,
        skip: loadOptions.skip,
        take: loadOptions.take,
        sort: JSON.stringify(loadOptions.sort),
        doc_query_params: JSON.stringify(docQueryParams.value)
      }

      // The custom store's load function can be triggered by many sources
      // changing the sort, selecting from a header filter dropdown, typing  into the filter row, changing the docQueryParams
      const { skip, take, ...comparableParams } = params
      const newSearch = !isEqual(comparableParams, lastParams.value)
      lastParams.value = comparableParams

      let response
      if (newSearch)
        response = await axios.get("/api/search_grid/start_search", { params })
      else
        response = await axios.get("/api/search_grid/get_page", { params })

      const documents = response.data.documents
      const totalCount = response.data.totalCount

      searchResultCount.value = totalCount
      if (newSearch) {
        fetchSearchHistoryItems()
        fetchSelectAllState()
        fetchTagFilterItems()
      }
      return {
        data: documents,
        totalCount: totalCount
      }
    },
    byKey: async (docId) => {
      const params = {
        cabinet_id: cabinetId.value,
        skip: 0,
        take: 1000,
        sort: '[]',
        doc_query_params: JSON.stringify(docQueryParams.value)
      }
      const response = await axios.get("/api/search_grid/start_search", { params })
      const documents = response.data.documents || []
      return documents.find((d) => d.doc_id === docId) ?? null
    },
    update: async (docId, newValue) => {
      const params = {
        mode_params: { mode: "document", doc_id: docId },
        fields: newValue
      }

      await axios.post('/api/edit_metadata', params)
      toastStore.showToastMessage('Metadata updated', 'success', 'add')
      return newValue
    }
  })

  const createHeaderFilterStore = (dataField) => {
    return {
      store: new CustomStore({
        key: 'value',
        load: async (loadOptions) => {
          const params = {
            data_field: dataField,
            search_text: loadOptions.searchValue || '',
            skip: loadOptions.skip ?? 0,
            take: loadOptions.take ?? 100,
          }

          const response = await axios.get("/api/search_grid/header_dropdown", { params })

          return {
            data: response.data.dropdownOptions,
            totalCount: response.data.totalCount
          }
        },
        byKey: async (key) => {
          const response = await axios.get("/api/search_grid/header_dropdown", {
            params: { data_field: dataField, search_text: key || '' }
          })
          const options = response.data.dropdownOptions || []
          const found = options.find((o) => (o && o.value === key) || o === key)
          return found != null ? found : (typeof key === 'object' ? key : { value: key, text: String(key) })
        }
      }),
      pageSize: 100
    }
  }

  return {
    basketCount,
    searchGridInstance,
    searchResultCount,
    selectedOnly,
    headerFilter,
    searchBarObjects,
    docQueryParams,
    activeDocQueryParams,
    setDocQueryParams,
    resetSearchAndFilter,
    searchGridDataStore,
    createHeaderFilterStore,
    tagFilterItems,
    searchHistoryItems,
    tagEditorItems,
    selectAllState,
    selectDocument,
    selectAll,
    editTags,
    editMetadata,
    fetchHeadlines,
    fetchFoldersByPath,
    emptyBasket,
    generateBasket,
    refreshData,
    refreshSearchResults,
    scrollToTop,
    lastParams,
    refreshSearchPageSidebarData
  }
})
